<template lang="pug">
span
  span(@click="modal_open")
    slot(name="activator")
  v-dialog(v-model="isModalVisible" persistent max-width="800px")
    v-card
      title-bar(data-cy="column-sorting-title" dark) {{ header }}
      v-card-text
        v-layout
          v-flex.xs6.columns
            div
              v-text-field(v-model="filter" :label="searchLabel" append-icon="search")
            v-list.customize
              v-list-tile(v-for="(header, index) in filteredColumns" :key="index" v-if="!header.address_type")
                template(
                  v-if="header.value === ADDRESSES"
                )
                  address-tree-view(
                    :addresses="addressColumns"
                    ref='tree'
                    @change='toggleColumn(header, $event)'
                  )
                template(
                  v-else
                )
                  v-tooltip(bottom :disabled="!(maxColumnsExceeded && !header.visible)")
                    span {{ maxColumnsExceededToolTip }}
                    v-layout(slot="activator")
                      v-list-tile-action()
                        v-checkbox(
                          :data-cy="`column-sorting-checkbox-${index}`"
                          @change="toggleColumn(header)"
                          :input-value="header.visible"
                          :disabled="header.locked || header.pinned || (maxColumnsExceeded && !header.visible)"
                        )
                      v-list-tile-content.clickable-column(
                        @click="maxColumnsExceeded && !header.visible ? {} : toggleColumn(header)"
                        )
                        v-list-tile-title {{header.text || header.namePlural}}
          v-flex.xs6.ordering
            h3
              | {{ sectionHeader }} ({{orderedColumns.length}})
            h5(v-if="sectionSubheader") {{ sectionSubheader }}
            sortable-list(
              v-model="orderedColumns"
              lockAxis="y"
              axis="y"
              appendTo=".sortable-list"
              helperClass="dragging"
              @input="orderItems"
            )
              sortable-item(
                v-for="(column,index) in orderedColumns"
                :index="index"
                :key="index"
                :pinnable="pinnableColumns && index < maxPinnableColumns"
                :pinned="column.pinned"
                @pin="pinColumn(index, $event)"
              )
                span.text-truncate {{column.text || column.namePlural }}
            span.note {{ note }}
      v-card-actions.px-4.py-3
        v-spacer
        v-btn(v-if="type !== 'vitals'" @click="removeAllColumns" flat)
         | {{ removeText }}
        v-btn(@click="cancel" color="primary" outline) Cancel
        v-btn(data-cy="column-sorting-apply" @click="save" color="primary") Apply
</template>

<script>
import Modal from '@/Core/mixins/modal';
import SortableList from '@/Core/components/sortable/SortableList';
import SortableItem from '@/Core/components/sortable/SortableItem';
import TitleBar from '@/Core/components/TitleBar';
import AddressTreeView from './AddressTreeView';

const SortTypes = Object.freeze({
  Columns: 'columns',
  Favorites: 'favorites',
  Vitals: 'vitals',
  Report: 'report',
});
const ADDRESS = 'address';

export default {
  components: {
    SortableList,
    SortableItem,
    TitleBar,
    AddressTreeView,
  },
  mixins: [
    Modal,
  ],
  props: {
    currentColums: {},
    value: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: SortTypes.Columns,
      validator: value => {
        return Object.values(SortTypes).indexOf(value) !== -1;
      },
    },
    note: {
      type: String,
    },
    pinnableColumns: {
      type: Boolean,
      default: false,
    },
    maxPinnableColumns: {
      type: Number,
      default: 3,
    },
    removeAllColumnsButton: {
      type: Boolean,
      default: false,
    },
    maxColumns: {
      type: Number,
      default: 99,
    },
    sectionSubheader: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      filter: '',
      isModalVisible: false,
      columnsCopy: [],
      orderedColumns: [],
      maxColumnsExceededToolTip: 'To add the current selection, please de-select one or more vitals',
      isSorted: false,
    };
  },
  beforeMount() {
    this.orderedColumns = this.value.filter(f => f.visible);
  },
  created(){
    this.ADDRESSES = 'addresses';
  },
  updated(){
    if(!this.isSorted && this.currentColums){
      this.orderedColumns = this.currentColums;
    }
  },
  computed: {
    header() {
      return {
        [SortTypes.Columns]: 'Customize columns',
        [SortTypes.Favorites]: 'Favorite fields',
        [SortTypes.Vitals]: 'Customize vitals',
        [SortTypes.Report]: 'Customize report table',
      }[this.type];
    },
    sectionHeader() {
      return {
        [SortTypes.Columns]: 'Selected columns',
        [SortTypes.Favorites]: 'Favorite information',
        [SortTypes.Vitals]: 'Selected vitals',
        [SortTypes.Report]: 'Selected types',
      }[this.type];
    },
    removeText() {
      return {
        [SortTypes.Columns]: 'Remove all columns',
        [SortTypes.Favorites]: 'Remove all favorites',
      }[this.type];
    },
    searchLabel() {
      return {
        [SortTypes.Columns]: 'Search available fields...',
        [SortTypes.Favorites]: 'Search available fields...',
        [SortTypes.Vitals]: 'Search available vitals...',
        [SortTypes.Report]: 'Search available types...',
      }[this.type];
    },
    visibleColumns() {
      return this.columns.filter(column => column.visible === true);
    },
    filteredColumns() {
      return this.columns.filter(item => {
        if (this.filter && item.type != ADDRESS) {
          if (item.text) return item.text.toLowerCase().match(this.filter.toLowerCase());
          return item.namePlural.toLowerCase().match(this.filter.toLowerCase());
        }
        if(Object.keys(this.addressColumns).length == 0 && item.type == ADDRESS && item.value == this.ADDRESSES) return;
        return item;
      });
    },
    maxColumnsExceeded() {
      return this.visibleColumns.length >= this.maxColumns;
    },
    columns: {
      get() {
        this.columnsCopy = this.value.map(column => Object.assign({}, column));
        return this.columnsCopy;
      },
      set(newValue) {
        this.columnsCopy = newValue;
      },
    },
    addressColumns() {
      let columns = {};
      this.columns.forEach(column => {
        if (column.type === ADDRESS && column.value != this.ADDRESSES) {
          if (this.filter && column.text) {
            if(!column.text.toLowerCase().match(this.filter.toLowerCase())) return;
          }
          if (!columns[column.address_type]) columns[column.address_type] = [];
          columns[column.address_type].push(column);
        }
      });
      return columns;
    },
  },
  watch: {
    visibleColumns() {
      if(!this.currentColums){
        return  this.orderedColumns = this.visibleColumns.sort((item, prev) => {
          return item.order - prev.order;
        });
      }
      this.visibleColumns.forEach(item => {
        let name = item.name;
        if(!this.currentColums.some(item => item.name === name)){
          this.currentColums.push(item);
        }
      });
      this.currentColums.forEach((item, index) => {
        let name = item.name;
        if(!this.visibleColumns.some(item => item.name === name)){
          this.currentColums.splice(index, 1);
        }
      });
      this.orderedColumns = this.currentColums;
    },
  },
  methods: {
    pinColumn(index, isPinned) {
      for (let i = 0; i < this.orderedColumns.length; i++) {
        if (isPinned) {
          this.orderedColumns[i].pinned = (i <= index);
        } else {
          this.orderedColumns[i].pinned = (i < index);
        }
      }
    },
    /**
     * Toggles the column's visibility
     * @param  {Object} header the header
     * @return {}
     */
    toggleColumn(header, node = '') {
      this.isSorted = true;
      if (header.locked || header.pinned) {
        return false;
      }
      if (header.value === this.ADDRESSES) {
        const column = this.columns.find(item => item.text === node)
        header = column;
      }
      header.visible = !header.visible;
      // put vital at the end of the array
      if (!header.visible) header.order = this.visibleColumns.length;
    },
    /**
     * Clears all columns except locked ones
     * @return {}
     */
    removeAllColumns() {
      if (this.$refs.tree && this.$refs.tree.length) {
        this.$refs.tree[0].unselectAll();
      }
      this.currentColums = [];
      this.columns.forEach(item => {
        if (!item.locked) {
          item.pinned = false; // reset pinned state
          item.visible = false;
        }
      });
    },
    /**
     * When reordering the selected columns, set the order for the
     * full list of columns as well so they are both in sync.
     */
    orderItems() {
      this.isSorted = true;
      let unpinRest = false;
      for (let i = 0; i < this.orderedColumns.length; i++) {
        // unpin all columns that come after
        // first unpinned column (i.e. no pin gaps)
        if (!this.orderedColumns[i].pinned) {
          unpinRest = true;
        }
        if (unpinRest) {
          this.orderedColumns[i].pinned = false;
        }
        const columnIndex = this.columns.findIndex(item => {
          if(item.type == ADDRESS) return item.text === this.orderedColumns[i].text;
          if (item.value && !item.custom) return item.value === this.orderedColumns[i].value;
          if (item.id) return item.id === this.orderedColumns[i].id;
          return item.type === this.orderedColumns[i].type;
        });
        this.columns[columnIndex].order = i;
      }
    },
    save() {
      this.orderItems();
      const sortedColumns = this.columns.sort((a, b) => {
        return (a.order === undefined) - (b.order === undefined)
          || +(a.order > b.order) || -(a.order < b.order);
      });
      this.$emit('input', sortedColumns);
      this.isSorted = false;
      this.modal_close();
      this.filter = '';
    },
    cancel() {
      this.isSorted = false;
      this.filter = '';
      this.columns = this.value;
      if(this.$refs.tree) this.$refs.tree[0].toggleUpdateVisibility();
      this.modal_close();
    },
  },
};
</script>

<style scoped lang="scss">
  .v-dialog {
    overflow: hidden;

    h3, h5 {
      color: #999;
      text-transform: uppercase;
    }

    & > .v-card {
      .customize /deep/ .v-list__tile {
        display: inline-block;
      }
      .customize .v-list__tile .v-treeview {
        position: relative;
        right: 25px;
      }
      & > .v-card__text {
        display: flex;
        overflow: hidden;
        flex-direction: column;

        .wrapper {
          padding: 0;
          flex: 1 1;
        }
        .layout {
          max-height: 370px;
          padding: 0;
        }
        .flex {
          padding: 0 1em;
        }
      }
      & > .v-card__actions {
        display: flex;
        justify-content: flex-end;
      }
    }
  }

  .columns {
    display: flex;
    overflow: hidden;
    flex-direction: column;
    border-right: 1px solid #eee;

    .v-input {
      flex: 1 1 15%;
    }

    .v-list {
      overflow: auto;
      max-width: 100%;
      .v-list__tile {
        padding: 0;
        cursor: pointer;

        .v-list__tile__action {
          min-width: 32px;
        }
      }
    }
  }

  .ordering {
    display: flex;
    overflow: hidden;
    flex-direction: column;

    .sortable-list {
      margin-top: 1em;
      padding: 0 1em 0 0;
      flex: 1 1;
      overflow-y: auto;
      list-style: none;

      .sortable-item, .dragging {
        display: flex;
        align-items: center;
        padding: 3px 5px;
        background-color: #EAF0F6;
        border: 1px solid #CBD6E2;
        border-radius: 2px;
        margin-bottom: .5em;
        cursor: move;
      }

      .dragging {
        opacity: .75;
      }
    }
    .note {
      text-align: right;
      color: #999;
      font-size: 12px;
    }
  }

  .clickable-column {
    cursor: pointer;
  }
</style>
